import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Chip } from '@/ui/primitives';
import type { GrupoEconomico } from '../schemas';

interface Props {
  grupos: GrupoEconomico[];
  search: string;
  onSelect: (id: number) => void;
}

function formatCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return '—';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function GrupoList({ grupos, search, onSelect }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter(
      (g) =>
        g.nome.toLowerCase().includes(q) ||
        (g.cnpj ?? '').replace(/\D/g, '').includes(q.replace(/\D/g, '')),
    );
  }, [grupos, search]);

  const columns = useMemo<ColumnDef<GrupoEconomico>[]>(
    () => [
      {
        id: 'nome',
        accessorKey: 'nome',
        header: 'Grupo Econômico',
        cell: ({ row }) => (
          <span className="text-sm text-text font-medium">{row.original.nome}</span>
        ),
      },
      {
        id: 'cnpj',
        accessorKey: 'cnpj',
        header: 'CNPJ',
        cell: ({ row }) => (
          <span className="text-sm text-text-2 tabular-nums">
            {formatCnpj(row.original.cnpj)}
          </span>
        ),
      },
      {
        id: 'clientes',
        accessorKey: 'clientes_count',
        header: 'Clientes',
        cell: ({ row }) => {
          const n = row.original.clientes_count ?? 0;
          if (n === 0) return <span className="text-xs text-text-muted">—</span>;
          return (
            <Chip tone="accent" size="sm">
              {n}
            </Chip>
          );
        },
      },
      {
        id: 'imoveis',
        accessorKey: 'imoveis_count',
        header: 'Imóveis',
        cell: ({ row }) => {
          const n = row.original.imoveis_count ?? 0;
          if (n === 0) return <span className="text-xs text-text-muted">—</span>;
          return (
            <Chip tone="neutral" size="sm">
              {n}
            </Chip>
          );
        },
      },
      {
        id: 'hectares',
        accessorKey: 'area_total_ha',
        header: 'Hectares',
        cell: ({ row }) => {
          const ha = row.original.area_total_ha ?? 0;
          if (!ha) return <span className="text-xs text-text-muted">—</span>;
          return (
            <Chip tone="info" size="sm">
              {ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
            </Chip>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden">
      <table className="ds-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  onClick={
                    h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined
                  }
                  className={h.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {h.column.getIsSorted() === 'asc' && ' ↑'}
                  {h.column.getIsSorted() === 'desc' && ' ↓'}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row.original.id)}
              className="cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
